import React, { useEffect } from "react";

/**
 * BidConfirmDialog — v3.99d.341y.
 *
 * Two-touch guard for every bid submission. Renders a lightweight
 * modal (no shadcn deps so it can drop into any surface — Forge
 * chip, listing card, auction detail — without a heavy dialog
 * root). The confirm button labels itself with the exact amount
 * the backend will receive so the number on the button and the
 * amount posted to /api/listings/{id}/bid never drift.
 *
 * Props:
 *   open        boolean       — controls visibility
 *   amount      number        — the bid the user is about to place
 *   title       string        — auction/listing title (for context)
 *   busy        boolean       — disables Confirm while the POST flies
 *   onCancel()  function      — invoked when the user dismisses
 *   onConfirm() function      — invoked when the user confirms
 *   testidRoot  string        — e.g. "quick-bid" / "chip-quickbid"
 */
export default function BidConfirmDialog({
  open, amount, title, busy = false,
  onCancel, onConfirm,
  testidRoot = "bid-confirm",
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
      if (e.key === "Enter" && !busy) onConfirm?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel, onConfirm]);

  if (!open) return null;

  const pretty = (n) => {
    const v = Number(n) || 0;
    // Show whole dollars if integral, else 2 decimals — matches
    // the backend's rounding contract for next_min_bid.
    return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
  };

  return (
    <div
      data-testid={`${testidRoot}-modal`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        display: "flex", alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(6px)",
        padding: "16px",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testidRoot}-title`}
        style={{
          width: "min(420px, 100%)",
          background: "var(--card, #0e0e0e)",
          color: "var(--text, #f5f5f5)",
          border: "1px solid var(--border, #262626)",
          borderRadius: "2px",
          padding: "20px 20px 16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 24px rgba(234,88,12,0.15)",
        }}
      >
        <div
          id={`${testidRoot}-title`}
          className="text-sm sm:text-base font-semibold"
          style={{ letterSpacing: "0.01em" }}
        >
          Confirm your <span
                          data-testid={`${testidRoot}-amount`}
                          style={{ color: "#ea580c" }}>
            {pretty(amount)}
          </span> bid on {title || "this auction"}?
        </div>
        <div
          className="mt-2 text-xs sm:text-sm"
          style={{ color: "var(--text-2, #a3a3a3)" }}
        >
          Bids are binding and cannot be withdrawn.
        </div>
        <div className="mt-5 flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            data-testid={`${testidRoot}-cancel-btn`}
            className="px-3 py-2 text-xs sm:text-sm"
            style={{
              background: "transparent",
              color: "var(--text-2, #a3a3a3)",
              border: "1px solid var(--border, #262626)",
              borderRadius: "2px",
              opacity: busy ? 0.5 : 1,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => !busy && onConfirm?.()}
            disabled={busy}
            data-testid={`${testidRoot}-confirm-btn`}
            className="px-4 py-2 text-xs sm:text-sm font-semibold"
            style={{
              background: "#ea580c",
              color: "#fff",
              border: "1px solid #ea580c",
              borderRadius: "2px",
              opacity: busy ? 0.6 : 1,
              cursor: busy ? "not-allowed" : "pointer",
              boxShadow: busy ? "none" : "0 0 12px rgba(234,88,12,0.35)",
            }}
          >
            {busy ? "Placing…" : `Confirm ${pretty(amount)} Bid`}
          </button>
        </div>
      </div>
    </div>
  );
}
